import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { EventFacts } from "../components/EventSummary.js";
import { Button, Field, inputStyle, Selector } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { SPORT_LABEL } from "../lib/sport.js";
import { fetchMyChildren } from "../lib/members.js";
import { createEvent, createTournament, fetchTournaments } from "../lib/tournaments.js";
import {
  EVENT_CATEGORY_LABEL,
  EVENT_FORMAT_LABEL,
  entryRefusal,
  refusalIsPlayingAs,
  isKidsCategory,
  isSeniorCategory,
  ordinal,
  registeredEntries,
  PAIRING_HINT,
  PAIRING_LABEL,
  waitlistPosition,
  type EventCategory,
  type EventFormat,
  type EventOrdering,
  type Kind,
  type Member,
  type PairingStrategy,
  type Tournament,
  type TournamentEvent,
} from "../lib/types.js";

// Not the pool: an event is one because its tournament is an auction, never by choice, and
// the database refuses it anywhere else.
const FORMAT_OPTIONS = (Object.keys(EVENT_FORMAT_LABEL) as EventFormat[])
  .filter((value) => value !== "auction_pool")
  .map((value) => ({ value, label: EVENT_FORMAT_LABEL[value] }));

/**
 * Every tournament and its events, as a list of doors.
 *
 * Nothing happens on this screen except creating structure. Registering, notes and the draw
 * all live one tap in, on the event's own page — a list row is the wrong place to type
 * "only available in the morning", and having the register button in two places would mean
 * two flows to keep honest.
 *
 * A tournament has no sport of its own; its events do. Every tournament in the community
 * shows up here, but `fetchTournaments` has already stripped the events belonging to other
 * sports, and this screen never mentions that they existed. A badminton player has one app
 * and it is about badminton.
 */
export function Tournaments() {
  const { member } = useAuth();
  const canOrganise = hasRole(member, "organiser");

  const { data, isPending, error } = useQuery({
    queryKey: ["tournaments"],
    queryFn: fetchTournaments,
  });

  const household = useHousehold();
  // A finished tournament is not an event anybody can still enter. It is still reachable by its
  // own link, and from the achievement on its MVP's profile.
  const tournaments = (data ?? []).filter((tournament) => !isFinished(tournament));

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <PageHeader
        eyebrow={SPORT_LABEL}
        title="Events"
        action={
          canOrganise ? (
            <span className="flex items-center gap-3">
              {/* The way back to tournaments that have been finished, which the list below no
                  longer shows. Organisers only; the page is signposted the same way. */}
              <Link
                to="/tournaments/past"
                className="flex min-h-[44px] items-center whitespace-nowrap text-[13px] font-semibold underline"
              >
                Past tournaments
              </Link>
              <Link
                to="/tournaments/new"
                className="flex min-h-[44px] items-center gap-1 whitespace-nowrap border-[1.5px] px-2.5 text-[13px] font-semibold"
                style={{ borderColor: "var(--color-line)" }}
              >
                <span aria-hidden>+</span> New tournament
              </Link>
            </span>
          ) : null
        }
      />

      {isPending ? <p className="micro">Loading…</p> : null}
      {error ? (
        <p className="my-4 text-sm" style={{ color: "var(--color-attention)" }}>
          Couldn&rsquo;t load tournaments. Try again.
        </p>
      ) : null}

      {!isPending && tournaments.length === 0 ? (
        <p className="my-4 text-sm text-ink-muted">
          No tournaments yet.{" "}
          {canOrganise ? "New tournament, top right, starts one." : "Check back once one's set up."}
        </p>
      ) : null}

      {tournaments.map((tournament) => (
        <TournamentCard key={tournament.id} tournament={tournament} household={household} />
      ))}
    </main>
  );
}

/** You, then the children you act for — everyone this screen can say "is in" about. */
export type Household = (Member & { self: boolean })[];

/**
 * The children you enter things for, so a row can say "Ishaan is in" as well as "you're
 * in" — for a parent that is the more common question on a list of events.
 */
export function useHousehold(): Household {
  const { member } = useAuth();
  const { data: children = [] } = useQuery({
    queryKey: ["children", member?.id],
    queryFn: () => fetchMyChildren(member!.id),
    enabled: Boolean(member),
  });
  return member ? [{ ...member, self: true }, ...children.map((c) => ({ ...c, self: false }))] : [];
}

function TournamentCard({
  tournament,
  household,
}: {
  tournament: Tournament;
  household: Household;
}) {
  const events = tournament.events;
  const finished = isFinished(tournament);

  return (
    <article className="mb-10">
      {/* The heading is the link to the tournament's own page, which is what gets shared
          when somebody wants to send the whole weekend rather than one event. */}
      <Link to={`/tournaments/${tournament.id}`} className="block">
        <TournamentBanner tournament={tournament} finished={finished} heading="h2" />
      </Link>
      {tournament.description ? (
        <p className="mt-3 whitespace-pre-line text-sm text-ink-muted">{tournament.description}</p>
      ) : null}

      <div>
        {events.length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted">No events yet.</p>
        ) : (
          events.map((event) => (
            <EventRow
              key={event.id}
              event={event}
              household={household}
              finished={finished}
              showFormat={!tournament.auction}
            />
          ))
        )}
      </div>
    </article>
  );
}

/**
 * The tournament as a scoreboard would post it: the date on the left, the name beside it. One
 * still to come is a solid ink block; one that is over is only ruled.
 */
export function TournamentBanner({
  tournament,
  finished,
  heading: Heading,
  note,
}: {
  tournament: Tournament;
  finished: boolean;
  heading: "h1" | "h2";
  /** One more fact for the meta line, such as how many events there were. */
  note?: string;
}) {
  const date = dateBlock(tournament.startsOn, tournament.endsOn);

  return (
    <div
      className="flex items-stretch gap-3.5 p-3.5"
      style={
        finished
          ? { border: "2px solid var(--color-line)", color: "var(--color-ink)" }
          : { background: "var(--color-line)", color: "var(--color-ground)" }
      }
    >
      <span
        className="flex min-w-[76px] shrink-0 flex-col justify-center whitespace-nowrap border-r pr-3"
        style={{
          borderColor: finished ? "var(--color-line-muted)" : "var(--color-ink-muted)",
        }}
      >
        <span className="font-display text-[40px] leading-[0.85]" style={{ fontWeight: 900 }}>
          {date.days}
        </span>
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">
          {date.month}
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
        <Heading
          className="font-display uppercase"
          style={{ fontSize: 28, fontWeight: 800, lineHeight: 0.95 }}
        >
          {tournament.name}
        </Heading>
        <span
          className="font-mono text-[10px] uppercase tracking-[0.1em]"
          style={{
            color: finished ? "var(--color-ink-muted)" : "var(--color-on-ink-muted)",
          }}
        >
          {/* The date block beside this already says when; repeating "21–22 NOV 2026" here
              was the same fact twice. Only the year is added, and only when it isn't this
              one. Auction is said once here rather than on every event row below. */}
          {[
            finished ? "Finished" : null,
            tournament.startsOn.slice(0, 4) !== today().slice(0, 4)
              ? tournament.startsOn.slice(0, 4)
              : null,
            tournament.auction ? "Teams by auction" : null,
            tournament.private ? "Invite only" : null,
            tournament.venue,
            note ?? null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      {/* Always reserved, logo or not, so a banner without one is the same height and its
          date and name sit where they would with one. The ringed, ground-coloured backing
          is what lets a dark or transparent logo read on the ink banner as well as the
          ruled one; with no logo the space stays but nothing is drawn in it. */}
      <span
        className="flex size-14 shrink-0 items-center justify-center self-center overflow-hidden"
        style={
          tournament.logoUrl
            ? { background: "var(--color-ground)", border: "1px solid var(--color-line-muted)" }
            : undefined
        }
        data-testid="tournament-logo-slot"
      >
        {tournament.logoUrl ? (
          <img
            src={tournament.logoUrl}
            alt={`${tournament.name} logo`}
            className="size-full object-contain"
          />
        ) : null}
      </span>
    </div>
  );
}

/**
 * Over once an organiser has said so. Not worked out from dates: a tournament whose last day
 * has passed stays listed, and open to its results, until somebody finishes it.
 */
export function isFinished(tournament: Tournament): boolean {
  return tournament.finishedAt !== null;
}

/**
 * One event, as a link to its page.
 *
 * The whole row is the target rather than a "view" link at the end of it — a tap area the
 * size of the row on a phone, and no hunting for the one word that is clickable. A stamp on
 * the right answers "am I in Friday's?" without opening anything: in, queued, or — when
 * entries are open and somebody in your household could enter — an invitation to register.
 *
 * A row that says Register goes straight to the register screen. Somebody tapping the word
 * Register means to register, and the event page in between was one more screen to read
 * before finding the button again.
 */
export function EventRow({
  event,
  household,
  finished,
  showFormat = true,
}: {
  event: TournamentEvent;
  household: Household;
  finished: boolean;
  /** Off in an auction tournament, where every event is an auction pool and the banner says so. */
  showFormat?: boolean;
}) {
  const stamp = eventStamp(event, household, finished);

  return (
    <Link
      to={
        stamp?.tone === "open" || stamp?.tone === "playing-as"
          ? `/events/${event.id}/register`
          : `/events/${event.id}`
      }
      className="block border-b py-3"
      style={{
        borderColor: "var(--color-line-muted)",
        color: "var(--color-ink)",
        minHeight: 80,
      }}
    >
      {/* Name, stamp and chevron on one line; the description underneath at full width.
          Beside the stamp it was squeezed into a column a few words wide. */}
      <span className="flex items-center gap-3">
        {/* Three levels, no more: the name, one line of facts, the description below.
            The eyebrow ("Juniors" said again what "Boys" says) and the "doesn't count"
            sentence are gone — the latter is an Unrated tag on the facts line. */}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-base font-bold">{event.name}</span>
          <EventFacts event={event} showFormat={showFormat} />
        </span>

        {stamp ? (
          stamp.tone === "in" ? (
            <span
              className="font-mono shrink-0 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em]"
              style={{
                background: "var(--color-line)",
                color: "var(--color-ground)",
              }}
            >
              {stamp.label}
            </span>
          ) : stamp.tone === "queue" || stamp.tone === "playing-as" ? (
            <span className="font-mono shrink-0 border-[1.5px] border-dashed px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
              {stamp.label}
            </span>
          ) : (
            <span
              className="shrink-0 border-2 px-2.5 py-1.5 text-[13px] font-bold"
              style={{ borderColor: "var(--color-line)" }}
            >
              {stamp.label}
            </span>
          )
        ) : null}

        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          aria-hidden
          className="shrink-0"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </span>

      {/* Who it is for and when, read before deciding whether to tap at all. Two lines at
          most here; the event's own page has the rest. The "not how you play" hint that used
          to follow is the stamp's job now — Seniors only says it, and the register screen
          says why and how to change it. */}
      {event.description ? (
        <span className="mt-2 line-clamp-2 block whitespace-pre-line pr-7 text-sm leading-relaxed">
          {event.description}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * What the row's stamp says. You first, then your children by name; then, if nobody in the
 * household is entered and entries are open, whether anybody could be — by category, the
 * same check the register screen runs. Rating bounds are left to that screen, which is
 * where a refusal can be explained. A kids' event is always open to a grown-up, who may yet
 * add the child on the register screen.
 */
function eventStamp(
  event: TournamentEvent,
  household: Household,
  finished: boolean,
): { tone: "in" | "queue" | "open" | "playing-as"; label: string } | null {
  for (const person of household) {
    const entry = event.entries.find((e) => e.memberId === person.id);
    if (!entry) continue;
    const who = person.self ? "You're" : `${person.displayName.split(" ")[0]} is`;
    if (entry.status === "registered") return { tone: "in", label: `${who} in` };
    const place = waitlistPosition(event.entries, person.id) ?? 0;
    return { tone: "queue", label: `${who} ${ordinal(place)} in queue` };
  }

  // An event with no cutoff is open until the tournament is over, not for ever.
  const open =
    !finished && (event.entriesCloseAt === null || new Date(event.entriesCloseAt) > new Date());
  if (!open) return null;
  // A kids' event invites any grown-up in, children added or not. The register screen is where
  // a parent adds one, and a row that offers them nothing reads as "not for you".
  const parent = isKidsCategory(event.category) && household.some((p) => p.self && !p.junior);
  if (!parent && !household.some((person) => entryRefusal(event.category, person) === null)) {
    // Refused only for how you play: yours to change, so say so rather than show a dead row.
    const own = household.find((person) => person.self);
    const refusal = own ? entryRefusal(event.category, own) : null;
    if (refusal && refusalIsPlayingAs(refusal)) {
      return {
        tone: "playing-as",
        label: refusal.endsWith("seniors") ? "Seniors only" : "Adults only",
      };
    }
    return null;
  }

  const full = event.capacity !== null && registeredEntries(event.entries).length >= event.capacity;
  return { tone: "open", label: full ? "Join queue" : "Register" };
}

/** Today as YYYY-MM-DD in local time — `toISOString` is UTC, a day behind before 5:30am IST. */
const today = () => new Date().toLocaleDateString("en-CA");

/** "11–12" over "OCT" — the date as a scoreboard would post it. */
function dateBlock(startsOn: string, endsOn: string | null): { days: string; month: string } {
  const start = new Date(`${startsOn}T00:00:00`);
  const end = endsOn ? new Date(`${endsOn}T00:00:00`) : start;
  const month = (d: Date) => d.toLocaleDateString(undefined, { month: "short" });
  if (start.getMonth() === end.getMonth()) {
    return {
      days:
        start.getDate() === end.getDate()
          ? `${start.getDate()}`
          : `${start.getDate()}–${end.getDate()}`,
      month: month(start),
    };
  }
  return {
    days: `${start.getDate()}`,
    month: `${month(start)} → ${end.getDate()} ${month(end)}`,
  };
}

export function NewTournamentForm({
  onCreated,
  onCancel,
}: {
  onCreated: (tournamentId: string) => void;
  onCancel: () => void;
}) {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [venue, setVenue] = useState("");
  const [description, setDescription] = useState("");
  const [contactRequired, setContactRequired] = useState(false);
  const [auction, setAuction] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      createTournament(userId!, {
        name,
        startsOn,
        endsOn: endsOn || null,
        venue: venue || null,
        description: description || null,
        contactRequired,
        auction,
        private: isPrivate,
      }),
    onSuccess: (tournamentId) => {
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      onCreated(tournamentId);
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submit.mutate();
  }

  const valid = name.trim().length >= 2 && startsOn.length > 0;

  return (
    <form
      onSubmit={onSubmit}
      className="border-t-2 pt-4"
      style={{ borderColor: "var(--color-line)" }}
    >
      <Field label="Name">
        <input
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          style={inputStyle}
          maxLength={80}
        />
      </Field>

      {/* Stacked on phones: two date inputs side by side don't fit, and iOS won't shrink them. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
        <Field label="Starts">
          <input
            required
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Ends" hint="Optional — leave blank for a one-day event.">
          <input
            type="date"
            value={endsOn}
            onChange={(event) => setEndsOn(event.target.value)}
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="Venue">
        <input
          value={venue}
          onChange={(event) => setVenue(event.target.value)}
          style={inputStyle}
          maxLength={120}
        />
      </Field>

      <Field label="Description">
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          style={{ ...inputStyle, minHeight: 88, padding: "12px" }}
        />
      </Field>

      {/* Off by default, and switchable on the tournament page later — the database checks at
          registration and never re-checks an entry, so changing it costs nobody their place. */}
      <Field
        label="Contact details"
        hint={
          contactRequired
            ? "Entering needs a flat number and a phone number on file — a parent's, for a child. Only organisers and admins see them."
            : "Anybody can enter without them."
        }
      >
        <Selector
          label="Contact details"
          value={contactRequired ? "required" : "optional"}
          onChange={(value) => setContactRequired(value === "required")}
          options={[
            { value: "optional", label: "Not required" },
            { value: "required", label: "Contact details required" },
          ]}
        />
      </Field>

      {/* Asked here and nowhere else: the database refuses to change it once the tournament
          exists, because its events are pools only while it is an auction. */}
      <Field
        label="Teams"
        hint={
          auction
            ? "Players register into each event, and teams and partners come from the auction. Can't be changed once the tournament is created."
            : "Each event has its own format and draw. Can't be changed once the tournament is created."
        }
      >
        <Selector
          label="Teams"
          value={auction ? "auction" : "none"}
          onChange={(value) => setAuction(value === "auction")}
          options={[
            { value: "none", label: "Not a team league" },
            { value: "auction", label: "Picked by auction" },
          ]}
        />
      </Field>

      {/* Asked here and nowhere else, like the auction: a tournament that changed who can see
          it would be hiding what people had already seen and entered. */}
      <Field
        label="Who can see it"
        hint={
          isPrivate
            ? "Only the people you invite, their parents, and organisers. Everyone else will not know it exists. Can't be changed once the tournament is created."
            : "Every member. Can't be changed once the tournament is created."
        }
      >
        <Selector
          label="Who can see it"
          value={isPrivate ? "private" : "everyone"}
          onChange={(value) => setIsPrivate(value === "private")}
          options={[
            { value: "everyone", label: "Everyone" },
            { value: "private", label: "Invite only" },
          ]}
        />
      </Field>

      <div className="flex gap-2">
        <Button tone="primary" type="submit" disabled={!valid || submit.isPending}>
          {submit.isPending ? "Creating…" : "Create tournament"}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {submit.error ? (
        <p className="mt-4 text-sm" style={{ color: "var(--color-attention)" }}>
          {submit.error.message}
        </p>
      ) : null}
    </form>
  );
}

/**
 * In an auction tournament the event is a pool the auction picks from, so everything the
 * draw would need — format, partners, matches per player — is left out rather than asked for
 * and ignored.
 */
export function NewEventForm({
  tournamentId,
  auction,
  onDone,
}: {
  tournamentId: string;
  auction: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Kind>("doubles");
  const [format, setFormat] = useState<EventFormat>("round_robin");
  const [minRating, setMinRating] = useState("");
  const [maxRating, setMaxRating] = useState("");
  const [matchesPerPlayer, setMatchesPerPlayer] = useState("");
  const [capacity, setCapacity] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [pairing, setPairing] = useState<PairingStrategy>("snake");
  const [category, setCategory] = useState<EventCategory>("open");
  const [countsForRating, setCountsForRating] = useState(true);
  const [description, setDescription] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      createEvent({
        tournamentId,
        name,
        kind,
        format: auction ? "auction_pool" : format,
        ordering: (!auction && format === "group_knockout"
          ? "seed_finale"
          : "rest") as EventOrdering,
        minRating: minRating ? Number(minRating) : null,
        maxRating: maxRating ? Number(maxRating) : null,
        capacity: capacity ? Number(capacity) : null,
        // A `datetime-local` value has no zone. It is the organiser's own wall clock, which is
        // the club's, so it is read in the browser's zone rather than assumed to be UTC.
        entriesCloseAt: closesAt ? new Date(closesAt).toISOString() : null,
        pairing,
        category,
        countsForRating,
        description,
        matchesPerPlayer:
          !auction && format === "team_tie" && matchesPerPlayer ? Number(matchesPerPlayer) : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      onDone();
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submit.mutate();
  }

  const valid = name.trim().length >= 2;

  return (
    <form
      onSubmit={onSubmit}
      className="mt-4 border-t pt-4"
      style={{ borderColor: "var(--color-line-muted)" }}
    >
      <Field label="Event name" hint={`e.g. "${SPORT_LABEL} Men's Doubles, Intermediate"`}>
        <input
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          style={inputStyle}
          maxLength={80}
        />
      </Field>

      <Field
        label="Description"
        hint="Optional — start time, what to bring, anything else players should know."
      >
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          style={{ ...inputStyle, minHeight: 88, padding: "12px" }}
          maxLength={1000}
        />
      </Field>

      {/* Who may enter, held to by the database from the first registration on. Choosing a
          kids' category also turns counting off below, because that is what a kids' event
          nearly always wants; the organiser can turn it back on. */}
      <Field
        label="Who it's for"
        hint={
          isKidsCategory(category)
            ? "Juniors only. Adults can't enter; an organiser can still add anyone."
            : isSeniorCategory(category)
              ? "Seniors only. Other adults and juniors can't enter; an organiser can still add anyone."
              : category === "men" || category === "women"
                ? "Adults, seniors included. Juniors can't enter; an organiser can still add anyone."
                : "Anybody can enter."
        }
      >
        <Selector
          label="Who it's for"
          value={category}
          onChange={(value) => {
            const next = value as EventCategory;
            setCategory(next);
            setCountsForRating(!isKidsCategory(next));
          }}
          options={(Object.keys(EVENT_CATEGORY_LABEL) as EventCategory[]).map((value) => ({
            value,
            label: EVENT_CATEGORY_LABEL[value],
          }))}
        />
      </Field>

      <div className="mb-6 flex gap-3">
        <Selector
          label="Singles or doubles"
          value={kind}
          onChange={(value) => setKind(value as Kind)}
          // Swiss is one person a side; the database refuses a doubles one, so it is not offered.
          options={[
            { value: "singles", label: "Singles" },
            ...(format === "swiss" ? [] : [{ value: "doubles", label: "Doubles" }]),
          ]}
        />
        {auction ? null : (
          <Selector
            label="Format"
            value={format}
            onChange={(value) => {
              setFormat(value as EventFormat);
              if (value === "swiss") setKind("singles");
            }}
            options={FORMAT_OPTIONS}
          />
        )}
      </div>

      {auction ? (
        <p className="-mt-3 mb-6 text-sm text-ink-muted">
          Teams and partners come from the auction.
        </p>
      ) : null}

      {!auction && format === "team_tie" ? (
        <Field
          label="Matches per player"
          hint="Optional — the draw asks again, once the teams are picked and the legal values are known."
        >
          <input
            type="number"
            min={1}
            value={matchesPerPlayer}
            onChange={(event) => setMatchesPerPlayer(event.target.value)}
            style={inputStyle}
          />
        </Field>
      ) : null}

      {/* Entries are solo, so a doubles event has to decide who partners whom — §5.4. Not
          shown for singles, and not for a team tie, which pairs people itself. */}
      {!auction && kind === "doubles" && format !== "team_tie" ? (
        <Field label="Partners" hint={PAIRING_HINT[pairing]}>
          <Selector
            label="Pairing"
            value={pairing}
            onChange={(value) => setPairing(value as PairingStrategy)}
            options={(Object.keys(PAIRING_LABEL) as PairingStrategy[]).map((value) => ({
              value,
              label: PAIRING_LABEL[value],
            }))}
          />
        </Field>
      ) : null}

      {/* Asked here and nowhere else: there is no event edit screen, and the database fixes the
          answer the moment the event has a match, because changing it afterwards would leave
          ratings standing that no rule could have produced. */}
      <Field
        label="Ratings"
        hint={
          countsForRating
            ? "Matches here move ratings, like any other."
            : "Recorded and in the table, but nobody's rating moves. For kids' events. Can't be changed once a match is played."
        }
      >
        <Selector
          label="Counts towards ratings"
          value={countsForRating ? "counts" : "does-not"}
          onChange={(value) => setCountsForRating(value === "counts")}
          options={[
            { value: "counts", label: "Counts towards ratings" },
            { value: "does-not", label: "Doesn't count towards ratings" },
          ]}
        />
      </Field>

      <Field
        label="Entries close"
        hint="Optional. After this nobody can enter or withdraw themselves — an organiser still can."
      >
        <input
          type="datetime-local"
          value={closesAt}
          onChange={(event) => setClosesAt(event.target.value)}
          style={inputStyle}
        />
      </Field>

      <Field label="Places" hint="Optional — leave blank and everyone who registers is in.">
        <input
          type="number"
          min={2}
          value={capacity}
          onChange={(event) => setCapacity(event.target.value)}
          style={inputStyle}
        />
      </Field>

      <div className="flex gap-4">
        <Field label="Min rating" hint="Optional">
          <input
            type="number"
            step="0.1"
            value={minRating}
            onChange={(event) => setMinRating(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Max rating" hint="Optional">
          <input
            type="number"
            step="0.1"
            value={maxRating}
            onChange={(event) => setMaxRating(event.target.value)}
            style={inputStyle}
          />
        </Field>
      </div>

      <div className="flex gap-2">
        <Button tone="primary" type="submit" disabled={!valid || submit.isPending}>
          {submit.isPending ? "Adding…" : "Add event"}
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
      </div>

      {submit.error ? (
        <p className="mt-4 text-sm" style={{ color: "var(--color-attention)" }}>
          {submit.error.message}
        </p>
      ) : null}
    </form>
  );
}
